import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { NOTIFICATION_AUTO_HIDE_MS } from '../constants/uiTiming';

interface NotificationProps {
  type: 'success' | 'error' | 'info';
  message: string;
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'center' | 'inline';
  targetElement?: HTMLElement | null;
}

const Notification: React.FC<NotificationProps> = ({ 
  type, 
  message, 
  isVisible, 
  onClose, 
  duration = NOTIFICATION_AUTO_HIDE_MS,
  position = 'top-right',
  targetElement
}) => {
  const notificationRef = useRef<HTMLDivElement>(null);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        setIsFading(true);
        // После завершения анимации исчезновения (0.5 сек) вызываем onClose
        setTimeout(() => {
          onClose();
          setIsFading(false);
        }, 500);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  useEffect(() => {
    if (isVisible && position === 'inline' && notificationRef.current) {
      // Принудительно делаем уведомление частью потока (inline),
      // даже если targetElement отсутствует на момент первого рендера
      const notification = notificationRef.current;
      notification.style.position = 'static';
      notification.style.top = '';
      notification.style.right = '';
      notification.style.left = '';
      notification.style.bottom = '';
      notification.style.transform = '';
      notification.style.zIndex = '10000';
    }
  }, [isVisible, position]);

  if (!isVisible) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✗';
      case 'info':
        return '✓';
      default:
        return '•';
    }
  };

  const getClassName = () => {
    const baseClass = 'notification';
    const positionClass = `notification--${position}`; // всегда добавляем модификатор позиции, включая inline
    const fadeOutClass = isFading ? 'notification--fade-out' : '';
    return `${baseClass} ${baseClass}--${type} ${positionClass} ${fadeOutClass}`.trim();
  };

  const getContainerStyle = (): React.CSSProperties => {
    if (position === 'inline') {
      return {
        position: 'relative' as const,
        display: 'block'
      };
    }
    return {};
  };

  const content = (
    <div ref={notificationRef} className={getClassName()}>
      <div className="notification-icon">{getIcon()}</div>
      <div className="notification-content">
        <p className="notification-message">{message}</p>
      </div>
    </div>
  );

  if (position === 'inline') {
    return <div style={getContainerStyle()}>{content}</div>;
  }

  // Рендер вне текущей иерархии, чтобы не обрезалось родителями с overflow/transform
  return ReactDOM.createPortal(content, document.body);
};

export default Notification; 